import {
  ComponentType,
  useState,
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  ChangeEventHandler,
  FocusEvent,
} from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import InputNumberFormat, {
  NumberFormatValues,
} from 'components/Shared/SearchFilter/NumberInputBox/InputNumberFormat';
import useNumberInputBoxStyles from './styles';

interface InputPropsProps {
  hasThousandSeparator?: boolean;
}

type PermittedTextFieldProps = Pick<TextFieldProps, 'id' | 'defaultValue' | 'placeholder'>;

type ExternalValueControlProps =
  | { onChange?: never; value?: never }
  | { onChange: (newValue?: number) => void; value: number };

export type NumberInputBoxProps = InputPropsProps &
  PermittedTextFieldProps &
  ExternalValueControlProps & {
    getPrefix?: (inputState: InputState) => string | undefined;
    getSuffix?: (inputState: InputState) => string | undefined;
    validateChange?: (value: number | undefined) => boolean;
  };

export interface InputState {
  isFocused?: boolean;
  numericValue?: number;
  stringValue: string;
}

export default function NumberInputBox({
  hasThousandSeparator = true,
  onChange,
  value,
  getPrefix,
  getSuffix,
  validateChange = defaultValidateChange,
  ...textFieldProps
}: NumberInputBoxProps): JSX.Element {
  function convertNumericValueToString(numericValue?: number): string {
    return numericValue?.toString() || '';
  }

  const initialStringValue = convertNumericValueToString(value);

  const [numericValue, setNumericValue] = useState<number | undefined>(value);
  const [stringValue, setStringValue] = useState<string>(initialStringValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setNumericValue(value);
    setStringValue(convertNumericValueToString(value));
  }, [value]);

  const handleChange = (
    _: ChangeEvent<HTMLInputElement>,
    { floatValue: newNumericVal, value: newStringVal }: NumberFormatValues
  ): void => {
    setStringValue(newStringVal);
    setNumericValue(newNumericVal);
  };

  const confirmInput = (): void => {
    const isValid = validateChange(numericValue);
    if (!isValid) {
      const initialNumericValue = value;
      setStringValue(initialStringValue);
      setNumericValue(initialNumericValue);
    } else {
      onChange?.(numericValue);
    }
  };

  const handleKeyPress = (ev: KeyboardEvent<HTMLInputElement>): void => {
    if (ev.key !== 'Enter') {
      return;
    }
    ev.preventDefault();
    confirmInput();
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    confirmInput();
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true);
    // ugly hack: wait until isFocused state is updated; required for min year prefix
    setTimeout(() => e.target.select(), 1);
  };

  const classes = useNumberInputBoxStyles();

  return (
    <TextField
      classes={classes}
      value={stringValue}
      onBlur={handleBlur}
      onFocus={handleFocus}
      InputProps={{
        inputComponent: InputNumberFormat as ComponentType,
      }}
      inputProps={{
        hasThousandSeparator,
        prefix: getPrefix?.({ isFocused, numericValue, stringValue }),
        suffix: getSuffix?.({ isFocused, numericValue, stringValue }),
      }}
      {...textFieldProps}
      onKeyPress={handleKeyPress}
      onChange={handleChange as ChangeEventHandler<HTMLInputElement>}
    />
  );
}

function defaultValidateChange(): boolean {
  return true;
}
